import { CalendarDays, CheckCircle2, Inbox, Plus, Search, Sun } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogContent, DialogSrTitle } from '@/components/ui/dialog';
import { PriorityDot } from '@/components/tasks/PriorityDot';
import { useTaskSearch } from '@/hooks/useTasks';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

interface Command {
  id: string;
  label: string;
  Icon: typeof Sun;
  run: () => void;
}

/**
 * ⌘K — navigate, create, or find a task.
 *
 * Deliberately small (spec §46: "Nepřehánět"). Four navigation commands, one
 * create command, and task search once you type two characters.
 */
export function CommandPalette({
  open,
  onOpenChange,
  onOpenTask,
  onCreateTask,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenTask: (taskId: string) => void;
  onCreateTask: () => void;
}) {
  const t = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent position="top" className="max-w-xl">
        <DialogSrTitle>{t('command.label')}</DialogSrTitle>
        {/* Radix unmounts a closed dialog, so the palette opens empty every
            time without an effect resetting it. */}
        <PaletteBody
          onClose={() => onOpenChange(false)}
          onOpenTask={onOpenTask}
          onCreateTask={onCreateTask}
        />
      </DialogContent>
    </Dialog>
  );
}

function PaletteBody({
  onClose,
  onOpenTask,
  onCreateTask,
}: {
  onClose: () => void;
  onOpenTask: (taskId: string) => void;
  onCreateTask: () => void;
}) {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');

  // The highlight belongs to a particular term; storing them together lets the
  // reset happen during render instead of in a follow-up effect.
  const [selection, setSelection] = useState({ term: '', index: 0 });
  const highlighted = selection.term === term ? selection.index : 0;

  const move = useCallback(
    (delta: number, length: number) => {
      if (length === 0) return;
      setSelection((current) => {
        const base = current.term === term ? current.index : 0;
        return { term, index: (base + delta + length) % length };
      });
    },
    [term],
  );

  const t = useT();
  const { data: results, isFetching } = useTaskSearch(term);
  const tasks = results?.items ?? [];

  const close = onClose;

  const commands: Command[] = [
    {
      id: 'create',
      label: t('command.newTask'),
      Icon: Plus,
      run: () => {
        close();
        onCreateTask();
      },
    },
    {
      id: 'today',
      label: t('command.goToday'),
      Icon: Sun,
      run: () => {
        close();
        navigate('/today');
      },
    },
    {
      id: 'upcoming',
      label: t('command.goUpcoming'),
      Icon: CalendarDays,
      run: () => {
        close();
        navigate('/upcoming');
      },
    },
    {
      id: 'backlog',
      label: t('command.goBacklog'),
      Icon: Inbox,
      run: () => {
        close();
        navigate('/backlog');
      },
    },
    {
      id: 'completed',
      label: t('command.goCompleted'),
      Icon: CheckCircle2,
      run: () => {
        close();
        navigate('/completed');
      },
    },
  ];

  const filteredCommands = term.trim()
    ? commands.filter((command) =>
        command.label.toLowerCase().includes(term.trim().toLowerCase()),
      )
    : commands;

  // One flat list so the arrow keys move through commands and results
  // continuously, rather than trapping in one section.
  const entries: { key: string; run: () => void; node: React.ReactNode }[] = [
    ...filteredCommands.map((command) => ({
      key: `cmd-${command.id}`,
      run: command.run,
      node: (
        <span className="flex items-center gap-2.5">
          <command.Icon className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
          <span className="text-sm">{command.label}</span>
        </span>
      ),
    })),
    ...tasks.map((task) => ({
      key: `task-${task.id}`,
      run: () => {
        close();
        onOpenTask(task.id);
      },
      node: (
        <span className="flex min-w-0 items-center gap-2.5">
          <PriorityDot priority={task.priority} />
          <span className="truncate text-sm">{task.title}</span>
          {task.status === 'COMPLETED' ? (
            <span className="shrink-0 text-xs text-content-subtle">
              {t('task.completedAt')}
            </span>
          ) : null}
        </span>
      ),
    })),
  ];

  return (
    <>
      <div className="flex items-center gap-2.5 border-b border-border px-3.5 py-2.5">
        <Search className="h-4 w-4 shrink-0 text-content-subtle" aria-hidden />
        <input
          autoFocus
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              move(1, entries.length);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              move(-1, entries.length);
            } else if (event.key === 'Enter') {
              event.preventDefault();
              entries[highlighted]?.run();
            }
          }}
          placeholder={t('command.placeholder')}
          aria-label={t('command.label')}
          className="w-full bg-transparent text-sm text-content outline-none placeholder:text-content-subtle"
        />
      </div>

      <div className="max-h-80 overflow-y-auto p-1">
        {entries.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-content-subtle">
            {isFetching ? t('common.searching') : t('command.noResults')}
          </p>
        ) : (
          <ul role="listbox" aria-label={t('command.results')}>
            {entries.map((entry, index) => (
              <li key={entry.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === highlighted}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    entry.run();
                  }}
                  onMouseEnter={() => setSelection({ term, index })}
                  className={cn(
                    'w-full rounded px-2.5 py-1.5 text-left transition-surface',
                    index === highlighted ? 'bg-surface-hover' : 'bg-transparent',
                  )}
                >
                  {entry.node}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
